import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { fetchProducts } from '../../services/api';

const SingleProductPage = () => {
  const { id } = useParams();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const data = await fetchProducts();
        const productList = data.products || [];
        const foundProduct = productList.find(p => p.product_id === parseInt(id));
        if (foundProduct) {
          setProduct(foundProduct);
        } else {
          setError('Product not found');
        }
        setLoading(false);
      } catch (err) {
        setError('Failed to load product details. Please try again later.');
        setLoading(false);
      }
    };

    fetchData();
  }, [id]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 py-12 px-4 sm:px-6 lg:px-8 flex justify-center items-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#1E4C9A]"></div>
      </div>
    );
  }

  if (error || !product) {
    return (
      <div className="min-h-screen bg-gray-50 py-12 px-4 sm:px-6 lg:px-8 flex justify-center items-center">
        <div className="text-center p-8 bg-white rounded-xl shadow-sm border border-red-100 max-w-md w-full">
          <svg className="w-16 h-16 text-red-500 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
          </svg>
          <h3 className="text-xl font-bold text-gray-900 mb-2">{error || 'Product not found'}</h3>
          <Link to="/products" className="inline-block mt-4 bg-[#1E4C9A] text-white px-6 py-2 rounded-lg hover:bg-blue-900 transition-colors">
            Back to Products
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8 sm:py-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        
        {/* Breadcrumbs */}
        <nav className="flex mb-8 text-sm text-gray-500">
          <ol className="flex items-center space-x-2">
            <li><Link to="/" className="hover:text-[#1A8D32] transition-colors">Home</Link></li>
            <li><span className="mx-2">/</span></li>
            <li><Link to="/products" className="hover:text-[#1A8D32] transition-colors">Products</Link></li>
            <li><span className="mx-2">/</span></li>
            <li className="text-gray-900 font-medium truncate max-w-[150px] sm:max-w-xs">{product.name}</li>
          </ol>
        </nav>

        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
          <div className="flex flex-col lg:flex-row">
            
            {/* Image Gallery */}
            <div className="w-full lg:w-1/2 bg-gray-100 p-4 sm:p-8 flex items-center justify-center">
              <div className="relative w-full aspect-square max-w-lg mx-auto overflow-hidden rounded-xl bg-white shadow-inner">
                <img 
                  src={product.image_url || '/assets/images/placeholder-product.jpg'} 
                  alt={product.alt_text || product.name}
                  className="absolute inset-0 w-full h-full object-cover transition-transform duration-300 hover:scale-105"
                  onError={(e) => {
                    e.target.onerror = null;
                    e.target.src = '/assets/images/error.jpeg';
                  }}
                />
              </div>
            </div>

            {/* Product Details */}
            <div className="w-full lg:w-1/2 p-6 sm:p-10 flex flex-col">
              <div className="mb-6">
                <div className="flex flex-wrap items-center gap-3 mb-3">
                  {product.available ? (
                    <span className="bg-green-100 text-green-800 text-sm font-semibold px-3 py-1 rounded-full border border-green-200">
                      In Stock
                    </span>
                  ) : (
                    <span className="bg-red-100 text-red-800 text-sm font-semibold px-3 py-1 rounded-full border border-red-200">
                      Out of Stock
                    </span>
                  )}
                  <div className="flex items-center bg-yellow-50 px-3 py-1 rounded-full border border-yellow-100">
                    <span className="text-yellow-500 flex items-center">
                      <svg className="w-4 h-4 fill-current" viewBox="0 0 20 20">
                        <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                      </svg>
                      <span className="ml-1 text-sm font-bold text-gray-900">{Math.round((product.rating || 0) * 10) / 10}</span>
                    </span>
                    <span className="text-sm text-gray-500 ml-1">({product.review_count || 0} reviews)</span>
                  </div>
                </div>

                {/* Categories */}
                {product.categories && product.categories.length > 0 && (
                  <div className="flex flex-wrap items-center gap-2 mb-4">
                    <span className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Categories:</span>
                    {product.categories.map((cat) => (
                      <Link
                        key={cat.category_id}
                        to={`/products?category=${encodeURIComponent(cat.name)}`}
                        className="bg-blue-50 text-[#1E4C9A] hover:bg-[#1E4C9A] hover:text-white text-xs font-semibold px-2.5 py-1 rounded-md border border-blue-100 transition-colors"
                      >
                        {cat.name}
                      </Link>
                    ))}
                  </div>
                )}
                
                <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4 leading-tight">{product.name}</h1>
                <div className="text-2xl sm:text-3xl font-extrabold text-[#1E4C9A] mb-6">Rs. {product.price?.toLocaleString()}</div>
                
                <div className="prose prose-sm sm:prose-base text-gray-600 mb-8 max-w-none">
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">Description</h3>
                  <p className="whitespace-pre-wrap">{product.description}</p>
                </div>
              </div>

              {product.specifications && Object.keys(product.specifications).length > 0 && (
                <div className="mb-8">
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">Specifications</h3>
                  <div className="bg-gray-50 rounded-lg p-4 border border-gray-100">
                    <ul className="divide-y divide-gray-200">
                      {Object.entries(product.specifications).map(([key, value]) => (
                        <li key={key} className="py-2 flex flex-col sm:flex-row sm:justify-between">
                          <span className="font-medium text-gray-700 capitalize">{key}:</span>
                          <span className="text-gray-600 sm:text-right">{value}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              )}

              <div className="mt-auto pt-6 border-t border-gray-100 flex flex-col sm:flex-row gap-4">
                <Link 
                  to={`/contact?product=${encodeURIComponent(product.name)}`}
                  className="flex-1 text-center bg-[#1A8D32] text-white py-3 px-6 rounded-lg font-semibold hover:bg-green-700 transition-colors shadow-sm flex items-center justify-center gap-2"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                  Inquire Now
                </Link>
                <Link 
                  to="/products"
                  className="text-center bg-gray-50 text-gray-700 py-3 px-6 rounded-lg font-semibold hover:bg-gray-100 hover:text-gray-900 border border-gray-200 transition-colors"
                >
                  Back to Catalog
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SingleProductPage;